import { environment, klineApiV2Domain } from "@/lib/constants.ts";
import {
  IBinanceKlineArray,
  IUniversalKline,
} from "@/lib/datafeed/binanceTypes.ts";
import { BINANCE_RESOLUTION_MAP, EXCHANGES } from "@/lib/datafeed/config.ts";
import { findLegsName, prepareFormula } from "@/lib/datafeed/helpers.ts";
import {
  fetchActivityDetector,
  fetchAltTimersBars,
  fetchBidAsk,
  fetchBidAskSpread,
  fetchCryptoDrift,
  fetchDogBars,
  fetchFundingRate,
  fetchHFTActivity,
  fetchHighestSizeOrder,
  fetchIdeasBars,
  fetchLayering,
  fetchLiquidationsBars,
  fetchManipulationBars,
  fetchMarketPowerBars,
  fetchMatrixSpread,
  fetchNetOiDelta,
  fetchOiNetSpike,
  fetchOiSpike,
  fetchOpenInterestBars,
  fetchOrderBookChanges,
  fetchPowerDelta,
  fetchPowerTradesBars,
  fetchPumpTrendBars,
  fetchRealizedBidAskSpread,
  fetchRetailPowerBars,
  fetchSantimentBars,
  fetchSpoofLayer,
  fetchSpotFutureSpread,
  fetchSpotKlineBars,
  fetchSpotVolumeBars,
  fetchSpreadBars,
  fetchStopKillerBars,
  fetchStrategyBars,
  fetchStrategyCM1Bars,
  fetchStrategyCM2Bars,
  fetchStrategyCM3Bars,
  fetchTickIndexBars,
  fetchTrendAssessment,
  fetchVolumeBars,
  recursiveApiCall,
} from "@/lib/datafeed/utils.ts";
import { Bar, IDatafeedChartApi } from "@tradingView/charting_library";
import Formula from "fparser";
import { websocketKlineConnection } from "@/lib/websocketKlineConnection";
import { v4 as uuidv4 } from "uuid";

export const getBars: IDatafeedChartApi["getBars"] = async (
  symbolInfo,
  resolution,
  periodParams,
  onHistoryCallback,
  onErrorCallback,
) => {
  const { from, to, countBack } = periodParams;
  // if (symbolInfo.exchange !== EXCHANGES.BINANCE && resolution.includes("S")) {
  //   onHistoryCallback([], { noData: true });
  //   return;
  // }
  try {
    let bars: Bar[] = [];
    switch (symbolInfo.exchange) {
      case EXCHANGES.BYBIT:
        bars = (
          await universalKlineAPI(
            symbolInfo.exchange,
            "FUTURES",
            symbolInfo.name,
            from,
            to,
            resolution,
            1000,
            countBack,
          )
        ).map((kline) => {
          return {
            time: kline.time,
            open: kline.open,
            high: kline.high,
            low: kline.low,
            close: kline.close,
            volume: kline.volume,
          } as Bar;
        });
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.BLOFIN:
        bars = (
          await universalKlineAPI(
            symbolInfo.exchange,
            "FUTURES",
            symbolInfo.name,
            from,
            to,
            resolution,
            1000,
            countBack,
          )
        ).map((kline) => {
          return {
            time: kline.time,
            open: kline.open,
            high: kline.high,
            low: kline.low,
            close: kline.close,
            volume: kline.volume,
          } as Bar;
        });
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.SYNTHETIC:
        console.log("get synthetic", symbolInfo.name);
        bars = await universalKlines(
          EXCHANGES.SYNTHETIC,
          "FUTURES",
          symbolInfo.name,
          from,
          to,
          resolution,
          1000,
          countBack,
        );
        onHistoryCallback(bars, { noData: false });
        return;

      case EXCHANGES.BINANCE:
        bars = (
          await universalKlineAPI(
            symbolInfo.exchange,
            "FUTURES",
            symbolInfo.name,
            from,
            to,
            resolution,
            1000,
            countBack,
          )
        ).map((kline) => {
          return {
            time: kline.time,
            open: kline.open,
            high: kline.high,
            low: kline.low,
            close: kline.close,
            volume: kline.volume,
          } as Bar;
        });
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.HFT_ACTIVITY:
        bars = await fetchHFTActivity(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.OINETLONGSPIKE:
        bars = await fetchOiNetSpike(
          symbolInfo.name,
          periodParams,
          resolution,
          "LONG",
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.TREND_ASSESSMENT:
        bars = await fetchTrendAssessment(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.ALT_TIMERS:
        bars = await fetchAltTimersBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.DOG:
        bars = await fetchDogBars(symbolInfo.name, periodParams, resolution);
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.TICK_INDEX:
        bars = await fetchTickIndexBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.LAYERING:
        bars = await fetchLayering(symbolInfo.name, periodParams, resolution);
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.OINETSHORTSPIKE:
        bars = await fetchOiNetSpike(
          symbolInfo.name,
          periodParams,
          resolution,
          "SHORT",
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.OISPIKE:
        bars = await fetchOiSpike(symbolInfo.name, periodParams, resolution);
        onHistoryCallback(bars, { noData: false });
        return;

      case EXCHANGES.OINETDELTA:
        console.log("get bars oi delta", symbolInfo.name);
        bars = await fetchNetOiDelta(symbolInfo.name, periodParams, resolution);
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.RETAIL_POWER:
        bars = await fetchRetailPowerBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.POWER_DELTA:
        bars = await fetchPowerDelta(symbolInfo.name, periodParams, resolution);
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.MARKET_POWER:
        bars = await fetchMarketPowerBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.IDEAS:
        bars = await fetchIdeasBars(symbolInfo.name, periodParams, resolution);
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.PUMP_TREND:
        bars = await fetchPumpTrendBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.POWER_TRADES:
        bars = await fetchPowerTradesBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.SANTIMENT:
        bars = await fetchSantimentBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.FUNDING_RATE:
        bars = await fetchFundingRate(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.STRATEGY:
        console.log("getbars", symbolInfo.name);
        bars = await fetchStrategyBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.STRATEGY_CM1:
        bars = await fetchStrategyCM1Bars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.STRATEGY_CM2:
        bars = await fetchStrategyCM2Bars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.STRATEGY_CM3:
        bars = await fetchStrategyCM3Bars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.SPREAD:
        bars = await fetchSpreadBars(symbolInfo.name, periodParams, resolution);
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.MANIPULATIONS:
        if (symbolInfo.name == "BTCUSD") {
          onHistoryCallback([], { noData: true });
        }
        bars = await fetchManipulationBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.STOP_KILLER:
        bars = await fetchStopKillerBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.OPEN_INTEREST:
        bars = await fetchOpenInterestBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        if (bars.length > 0) {
          onHistoryCallback(bars, { noData: false });
        } else {
          onHistoryCallback([], { noData: true });
        }
        return;
      case EXCHANGES.LIQUIDATIONS:
        bars = await fetchLiquidationsBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.BIDASKQUOTEDSPREAD:
        bars = await fetchBidAskSpread(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.HIGHESTSIZEORDER:
        bars = await fetchHighestSizeOrder(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.SPOOF_LAYER_UP:
        bars = await fetchSpoofLayer(
          symbolInfo.name,
          periodParams,
          resolution,
          "UP",
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.SPOOF_LAYER_DOWN:
        bars = await fetchSpoofLayer(
          symbolInfo.name,
          periodParams,
          resolution,
          "DOWN",
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.ORDERBOOKCHANGES:
        bars = await fetchOrderBookChanges(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.REALIZEDBIDASKSPREAD:
        bars = await fetchRealizedBidAskSpread(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.MATRIXSPREAD:
        bars = await fetchMatrixSpread(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.SPOTFUTURESPREAD:
        bars = await fetchSpotFutureSpread(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.VOLUME:
        bars = await fetchVolumeBars(symbolInfo.name, periodParams, resolution);
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.SPOT_VOLUME:
        bars = await fetchSpotVolumeBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.SPOT_KLINE:
        bars = await fetchSpotKlineBars(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.ASKORDERBOOKSUM1_5:
        bars = await fetchBidAsk(
          symbolInfo.name,
          periodParams,
          resolution,
          EXCHANGES.ASKORDERBOOKSUM1_5,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.ASKORDERBOOKSUM6_10:
        bars = await fetchBidAsk(
          symbolInfo.name,
          periodParams,
          resolution,
          EXCHANGES.ASKORDERBOOKSUM6_10,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.BIDORDERBOOKSUM1_5:
        bars = await fetchBidAsk(
          symbolInfo.name,
          periodParams,
          resolution,
          EXCHANGES.BIDORDERBOOKSUM1_5,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.ACTIVITY_DETECTOR:
        bars = await fetchActivityDetector(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.CRYPTO_DRIFT:
        bars = await fetchCryptoDrift(
          symbolInfo.name,
          periodParams,
          resolution,
        );
        onHistoryCallback(bars, { noData: false });
        return;
      case EXCHANGES.BIDORDERBOOKSUM6_10:
        bars = await fetchBidAsk(
          symbolInfo.name,
          periodParams,
          resolution,
          EXCHANGES.BIDORDERBOOKSUM6_10,
        );
        onHistoryCallback(bars, { noData: false });
        return;
    }

    onHistoryCallback(bars, { noData: false });
  } catch (error) {
    console.error(error);
    onErrorCallback("Error getting bars");
  }
};

export const lastBarsCache = new Map<string, Bar>();

export interface IParam {
  [s: string]: number;
}

async function universalKlineAPI(
  exchange: string,
  exchangeType: string,
  symbol: string,
  startTime: number,
  endTime: number,
  resolution: string,
  limit = 1000,
  countBack = 0,
): Promise<Bar[]> {
  const params = {
    exchange: exchange.toUpperCase(),
    exchangeType,
    startTime: (startTime * 1000).toString(),
    endTime: (endTime * 1000).toString(),
    timeFrame: BINANCE_RESOLUTION_MAP[resolution],
    symbol,
    limit: countBack.toString(),
  };

  const queryString = new URLSearchParams(
    params as Record<string, string>,
  ).toString();
  const url = `${environment.universalKlineAPI}?${queryString}`;
  const response = await fetch(url, {
    method: "GET",
  });

  const data = await response.json();
  return data.map((d: IUniversalKline) => ({
    time: Number(d.timestamp),
    open: Number(d.open),
    high: Number(d.high),
    low: Number(d.low),
    close: Number(d.close),
    volume: Number(d.volume),
  }));
}

async function universalKlines(
  exchange: string,
  exchangeType: string,
  symbol: string,
  startTime: number,
  endTime: number,
  resolution: string,
  limit = 1000,
  countBack = 0,
): Promise<Bar[]> {
  return new Promise((resolve, reject) => {
    const params = {
      symbol,
      interval: BINANCE_RESOLUTION_MAP[resolution],
      exchange: exchange.toUpperCase(),
      exchangeType,
      limit: countBack,
      params: {
        startTime: startTime * 1000,
        endTime: endTime * 1000,
      },
    };

    let resolved = false;

    const handleKline = (data: any) => {
      if (resolved) {
        return;
      }
      if (data && Array.isArray(data.result) && data.id === symbol) {
        resolved = true;
        websocketKlineConnection.unsubscribe(params, handleKline);
        const bars = data.result.map((d: IUniversalKline) => ({
          time: Number(d.timestamp),
          open: Number(d.open),
          high: Number(d.high),
          low: Number(d.low),
          close: Number(d.close),
          volume: Number(d.volume),
        }));
        resolve(bars);
      } else if (
        data &&
        data.id === symbol &&
        (!data.result || !Array.isArray(data.result))
      ) {
        resolved = true;
        websocketKlineConnection.unsubscribe(params, handleKline);
        resolve([]);
      }
    };

    websocketKlineConnection.subscribe(symbol, params, handleKline);
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        websocketKlineConnection.unsubscribe(params, handleKline);
        reject(new Error("Timeout waiting for kline data"));
      }
    }, 5000);
  });
}
